import React, { useEffect, useMemo, useState } from 'react';
import { ITaskFullObject } from '../Interfaces/ITask';
import { useResolvedRecord } from '../hooks/useResolvedRecord';
import { useGetTaskFullObject } from '../hooks/reactQuerys/TaskQueries';
import { TaskDocuments } from './TaskDocuments';
import { ITaskType } from '../Interfaces/ITaskType';
import { ITaskPriority } from '../Interfaces/ITaskPriority';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  debounce,
  Grid,
  ListItem,
  ListItemText,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { TaskComments } from './TaskComments';
import { TaskLogList } from './TaskLogList';
import { CustomeTextField } from '../generic/CustomeTextField';
import { IUser } from '../Interfaces/IUser';
import { IUserGroup, selectableUserGroups } from '../Interfaces/IUserGroup';
import {
  Business,
  Check,
  Group,
  Person,
  RestartAlt,
} from '@mui/icons-material';
import { ILegalTransactionFullObject } from '../Interfaces/ILegalTransaction';
import { ILegalClient } from '../Interfaces/ILegalClient';
import {
  useGetLegalTransactionFullObject,
  useSearchLegalTransactions,
} from '../hooks/reactQuerys/LegalTransactionQueries';
import { SearchType } from '../Interfaces/ISearchType';
import { legalTransactionPossibleSearchOptions } from '../LegalTransaction/LegalTransactionOverview';
import { useGetLegalClients } from '../hooks/reactQuerys/LegalClientQueries';
import { legalClientsPossibleSearchOptions } from '../Clients/ClientsSearch';

interface IProps {
  editTask: ITaskFullObject;
  setEditTask: React.Dispatch<React.SetStateAction<ITaskFullObject>>;
  taskTypes: ITaskType[];
  taskPriority: ITaskPriority[];
  users: IUser[];
  userGroups: IUserGroup[];
  legalClients?: ILegalClient[];
  legalTransactionFullObj?: ILegalTransactionFullObject;
  disableEdit: boolean;
  legalClient?: ILegalClient;
  disableMarkDone?: boolean;
}

export const EditTask: React.FC<IProps> = ({
  editTask,
  setEditTask,
  taskTypes,
  taskPriority,
  users,
  userGroups,
  legalClients,
  legalTransactionFullObj,
  disableEdit,
  legalClient,
  disableMarkDone,
}) => {
  const [isClosed, setIsClosed] = useState(false);
  const [selectedType, setSelectedType] = useState('user');
  // #1190/#1248: Reiter Details | Kommentare | Log
  const [activeTab, setActiveTab] = useState(0);
  // REMINDER LOGIK
  const [reminderMode, setReminderMode] = useState(
    editTask.Reminder_at ? 'custom' : 'null'
  );
  const [customReminder, setCustomReminder] = useState(
    editTask.Reminder_at ? new Date(editTask.Reminder_at) : null
  );
  const { legalTransactionFullObject, isFetchingLegalTransactionFullObject } =
    useGetLegalTransactionFullObject(
      legalTransactionFullObj != undefined
        ? undefined
        : editTask.idLegalTransaction === null
          ? undefined
          : editTask.idLegalTransaction
    );

  // Dokumente einer bestehenden Aufgabe lazy nachladen. Nötig für den Vorgang-
  // Flow (arbeitet rein lokal, ohne DocumentArray). Im Standalone-Flow hat
  // DialogTask das Fullobject inkl. DocumentArray bereits geladen -> kein Fetch.
  const shouldLoadTaskDocuments =
    editTask.idTask > 0 && editTask.DocumentArray === undefined;
  const { taskFullObject: taskDocumentsFullObject, isLoadingTaskFullObject } =
    useGetTaskFullObject(shouldLoadTaskDocuments ? editTask.idTask : undefined);
  const documentsLoading = shouldLoadTaskDocuments && isLoadingTaskFullObject;

  useEffect(() => {
    if (
      taskDocumentsFullObject &&
      taskDocumentsFullObject.idTask === editTask.idTask &&
      editTask.DocumentArray === undefined
    ) {
      setEditTask(prev => ({
        ...prev,
        DocumentArray: taskDocumentsFullObject.DocumentArray ?? [],
        // #1190/#1248: derselbe Fetch liefert auch Kommentare + Log
        CommentArray: taskDocumentsFullObject.CommentArray ?? [],
        LogArray: taskDocumentsFullObject.LogArray ?? [],
      }));
    }
  }, [
    taskDocumentsFullObject,
    editTask.idTask,
    editTask.DocumentArray,
    setEditTask,
  ]);

  const [searchUriLegalTransactions, setSearchUriLegalTransactions] = useState<
    string | undefined
  >(undefined);
  const [searchUriLegalClients, setSearchUriLegalClients] = useState<
    string | undefined
  >(undefined);

  const {
    legalTransactions,
    isLoadingLegalTransactions,
    abortSearchLegalTransactions,
  } = useSearchLegalTransactions(searchUriLegalTransactions);

  const {
    legalClients: loaedlegalClients,
    isLoadingLegalClients,
    refetchLegalClients,
    abortLegalClients,
  } = useGetLegalClients(legalClients ? undefined : searchUriLegalClients);

  const legalTransactionPreparedArray =
    legalTransactions && legalTransactionFullObject
      ? [legalTransactionFullObject, ...legalTransactions]
      : legalTransactions
        ? legalTransactions
        : legalTransactionFullObject
          ? [legalTransactionFullObject]
          : [];

  // Im Vorgang-Flow kommt der Vorgang als Prop (legalTransactionFullObj) rein,
  // daher wird legalTransactionFullObject NICHT gefetcht (bleibt undefined). Die
  // Mandanten-Optionen kommen dann direkt aus dem legalClients-Prop
  // (= assignedLegalClients = LegalClientArray des Vorgangs). Nur im Standalone-
  // Flow (kein legalClients-Prop) wird über die Suche/das gefetchte LT-Objekt
  // befüllt.
  const legalClientsPreparedArray = legalClients
    ? legalClients
    : loaedlegalClients && legalTransactionFullObject
      ? [
          ...legalTransactionFullObject.LegalClientArray,
          ...loaedlegalClients.results,
        ]
      : legalTransactionFullObject
        ? legalTransactionFullObject.LegalClientArray
        : loaedlegalClients
          ? loaedlegalClients.results
          : [];

  const currenLegalTransaction =
    legalTransactionFullObject || legalTransactionFullObj;

  const debouncedSearchLegalTransaction = useMemo(
    () =>
      debounce((event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.value !== '') {
          let searchQuery = '';
          for (
            let i = 0;
            i < legalTransactionPossibleSearchOptions.length;
            i++
          ) {
            searchQuery += `${legalTransactionPossibleSearchOptions[i].SearchOptionKey}:${event.target.value};`;
          }
          setSearchUriLegalTransactions(
            `search=${searchQuery}&searchtype=${SearchType.INCLUDES}`
          );
        } else {
          setSearchUriLegalTransactions(undefined);
        }
      }, 700),
    []
  );

  const debouncedSearchLegaClient = useMemo(
    () =>
      debounce((event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.value !== '') {
          let searchQuery = '';
          for (let i = 0; i < legalClientsPossibleSearchOptions.length; i++) {
            searchQuery += `${legalClientsPossibleSearchOptions[i].SearchOptionKey}:${event.target.value};`;
          }
          setSearchUriLegalClients(
            `search=${searchQuery}&searchtype=${SearchType.INCLUDES}`
          );
        } else {
          setSearchUriLegalClients(undefined);
        }
      }, 700),
    []
  );

  const getReminderDate = (mode: string) => {
    if (!editTask.Deadline_at) return null;
    const deadline = new Date(editTask.Deadline_at);
    const d = new Date(deadline);
    switch (mode) {
      case '1d':
        d.setDate(deadline.getDate() - 1);
        return d;
      case '2d':
        d.setDate(deadline.getDate() - 2);
        return d;
      case '1w':
        d.setDate(deadline.getDate() - 7);
        return d;
      case '1m':
        d.setMonth(deadline.getMonth() - 1);
        return d;
      case 'custom':
        return customReminder;
      case 'null':
      default:
        return null;
    }
  };

  // Reminder-Optionen mit Disabled-Status
  const getReminderOptionsWithDisabled = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (!editTask.Deadline_at) {
      return [
        { value: 'null', label: 'Keine Erinnerung', disabled: false },
        { value: 'custom', label: 'Eigenes Datum auswählen…', disabled: false },
      ];
    }
    const deadline = new Date(editTask.Deadline_at);
    deadline.setHours(0, 0, 0, 0);

    const calcDate: Record<string, () => Date> = {
      '1d': () => {
        const d = new Date(deadline);
        d.setDate(d.getDate() - 1);
        return d;
      },
      '2d': () => {
        const d = new Date(deadline);
        d.setDate(d.getDate() - 2);
        return d;
      },
      '1w': () => {
        const d = new Date(deadline);
        d.setDate(d.getDate() - 7);
        return d;
      },
      '1m': () => {
        const d = new Date(deadline);
        d.setMonth(d.getMonth() - 1);
        return d;
      },
      custom: () => today,
    };

    const options = [
      { value: 'null', label: 'Keine Erinnerung' },
      { value: '1d', label: '1 Tag vorher' },
      { value: '2d', label: '2 Tage vorher' },
      { value: '1w', label: '1 Woche vorher' },
      { value: '1m', label: '1 Monat vorher' },
      { value: 'custom', label: 'Eigenes Datum auswählen…' },
    ];

    return options.map(opt => {
      if (opt.value === 'null' || opt.value === 'custom')
        return { ...opt, disabled: false };
      const d = calcDate[opt.value]?.();
      const disabled = d && d <= today;
      return { ...opt, disabled };
    });
  };

  const combinedOptions = [
    ...users.map(user => ({
      type: 'Mitarbeiter',
      id: user.idUser,
      name: `${user.FirstName} ${user.LastName}`,
    })),
    ...selectableUserGroups(userGroups).map(group => ({
      type: 'Benutzergruppen',
      id: group.idUserGroup,
      name: group.UserGroup,
    })),
  ];

  // Zugeordneten Vorgang und Mandanten nachladen, wenn die Suchliste sie nicht
  // kennt. Sonst sieht das Feld leer aus, obwohl eine Zuordnung besteht — die
  // Suchliste ist beim Oeffnen der Maske leer (QS #7).
  const resolvedLegalTransaction = useResolvedRecord(
    '/legaltransaction',
    'idLegalTransaction',
    editTask.idLegalTransaction,
    legalTransactionPreparedArray
  );
  const resolvedLegalClient = useResolvedRecord(
    '/legalclient',
    'idLegalClient',
    editTask.idLegalClient,
    legalClientsPreparedArray
  );

  const formatClientName = (c: ILegalClient) =>
    c.idBusinessForm === 1 ? `${c.FirstName} ${c.LastName}` : c.LastName;

  const highlightedClientIds = React.useMemo(() => {
    return (
      currenLegalTransaction?.LegalClientArray?.map(c => c.idLegalClient) || []
    );
  }, [currenLegalTransaction]);

  // Der gewaehlte Mandant MUSS auch als Option vorhanden sein, sonst warnt MUI
  // ("none of the options match with value") und das Feld verliert seine
  // Beschriftung. Mandanten des Vorgangs stehen vorn — die sind beim Zuordnen
  // einer Aufgabe die wahrscheinliche Wahl.
  const clientOptions = React.useMemo(() => {
    const sorted = [...legalClientsPreparedArray].sort((a, b) => {
      const aMatch = highlightedClientIds.includes(a.idLegalClient);
      const bMatch = highlightedClientIds.includes(b.idLegalClient);
      return Number(bMatch) - Number(aMatch); // true zuerst
    });
    if (
      resolvedLegalClient &&
      !sorted.some(c => c.idLegalClient === resolvedLegalClient.idLegalClient)
    ) {
      return [resolvedLegalClient, ...sorted];
    }
    return sorted;
  }, [legalClientsPreparedArray, highlightedClientIds, resolvedLegalClient]);

  // #1250: Erledigte Aufgabe im Read-only-Modus — der Toggle wird zum
  // „Wieder öffnen"-Button (Reopen für jeden, ohne Frist-Limit).
  const showReopen = disableEdit && editTask.Closed_at !== null;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mt: 1,
          mb: 2,
          minHeight: 36.5,
        }}
      >
        {editTask.idTask > 0 && editTask.FirstName ? (
          <Typography variant='caption' color='text.secondary'>
            Erstellt von {editTask.FirstName} {editTask.LastName} am{' '}
            {new Date(editTask.Created_at).toLocaleDateString('de-DE')}
          </Typography>
        ) : (
          <span />
        )}
        {(disableMarkDone === undefined || disableMarkDone === false) && (
          <Button
            variant={
              editTask.Closed_at && !showReopen ? 'contained' : 'outlined'
            }
            color={editTask.Closed_at && !showReopen ? 'success' : 'primary'}
            startIcon={showReopen ? <RestartAlt /> : <Check />}
            onClick={() => {
              setEditTask(prev => ({
                ...prev,
                Closed_at: prev.Closed_at ? null : new Date(),
              }));
            }}
          >
            {showReopen
              ? 'Wieder öffnen'
              : editTask.Closed_at
                ? 'Erledigt'
                : 'Als erledigt markieren'}
          </Button>
        )}
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab label='Details' />
        <Tab
          label={
            editTask.CommentArray && editTask.CommentArray.length > 0
              ? `Kommentare (${editTask.CommentArray.length})`
              : 'Kommentare'
          }
        />
        {editTask.idTask > 0 && <Tab label='Log' />}
      </Tabs>

      {/* Details bleibt gemountet (display:none), damit Eingabezustände beim
          Reiterwechsel erhalten bleiben. */}
      <Box sx={{ display: activeTab === 0 ? 'block' : 'none' }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <CustomeTextField
              attr='Title'
              label='Aufgabentitel'
              object={editTask}
              setObject={setEditTask}
              type='string'
              required
              disabled={disableEdit}
            />
          </Grid>
          <Grid item xs={8}>
            <CustomeTextField
              attr='idTaskType'
              label='Aufgabentyp'
              object={editTask}
              setObject={setEditTask}
              type='select'
              disabled={disableEdit}
            >
              {taskTypes.map(type => (
                <MenuItem
                  key={`select-tasktype-${type.idTaskType}`}
                  value={type.idTaskType}
                >
                  {type.TaskType}
                </MenuItem>
              ))}
            </CustomeTextField>
          </Grid>
          <Grid item xs={4}>
            <CustomeTextField
              attr='idTaskPriority'
              label='Priorität'
              object={editTask}
              setObject={setEditTask}
              type='select'
              disabled={disableEdit}
            >
              {taskPriority.map(prio => (
                <MenuItem
                  key={`select-taskpriority-${prio.idTaskPriority}`}
                  value={prio.idTaskPriority}
                >
                  {prio.TaskPriority}
                </MenuItem>
              ))}
            </CustomeTextField>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item sm={12}>
            <Autocomplete
              multiple
              options={combinedOptions}
              groupBy={option => option.type}
              getOptionLabel={option => option.name}
              disabled={isClosed || disableEdit}
              renderInput={params => (
                <TextField
                  {...params}
                  required={
                    editTask.Users.length === 0 &&
                    editTask.UserGroups.length === 0
                  }
                  label={
                    <span>
                      <Group
                        style={{ verticalAlign: 'middle', marginRight: 8 }}
                      />
                      Benutzer und Benutzergruppen zuweisen
                    </span>
                  }
                  size='small'
                  variant='outlined'
                />
              )}
              renderOption={(props, option) => (
                <ListItem {...props} key={option.id}>
                  <ListItemText primary={option.name} />
                </ListItem>
              )}
              onChange={(_, newValue) => {
                const users = newValue
                  .filter(v => v.type === 'Mitarbeiter')
                  .map(v => v.id);

                const groups = newValue
                  .filter(v => v.type === 'Benutzergruppen')
                  .map(v => v.id);

                setEditTask({
                  ...editTask,
                  Users: users,
                  UserGroups: groups,
                });
              }}
              value={combinedOptions.filter(
                o =>
                  (o.type === 'Mitarbeiter' &&
                    editTask.Users?.includes(o.id)) ||
                  (o.type === 'Benutzergruppen' &&
                    editTask.UserGroups?.includes(o.id))
              )}
            />
          </Grid>
          <Grid item sm={12} sx={{ mb: -1 }}>
            <Typography variant='caption'>Zugeordnete Datensätze</Typography>
            {/* QS #7: "Es ist unklar, was genau dieser Bereich dort bedeutet —
                bezieht sich die Aufgabe auf den Mandanten? Kann der Mandant die
                Aufgabeninhalte sehen? Das muss verständlicher sein, sonst traut
                man sich nicht, die Funktion zu nutzen." Bewusst dauerhaft
                sichtbarer Text und kein Tooltip: wer sich nicht traut, fährt
                auch nicht mit der Maus darüber. */}
            <Typography
              variant='caption'
              color='text.secondary'
              display='block'
              sx={{ mt: 0.25 }}
            >
              Die Aufgabe erscheint dadurch zusätzlich in der Aufgabenliste des
              gewählten Vorgangs und des gewählten Mandanten. Beides ist ein
              interner Bezug — im Mandantenportal sind Aufgaben nicht sichtbar.
            </Typography>
          </Grid>
          <Grid item sm={6}>
            {legalTransactionFullObj ? (
              legalTransactionFullObj.Title
            ) : (
              <Autocomplete
                loading={isLoadingLegalTransactions}
                noOptionsText={
                  searchUriLegalTransactions === undefined
                    ? 'Tippen, um zu suchen…'
                    : isLoadingLegalTransactions
                      ? 'Suche…'
                      : 'Keine Treffer'
                }
                options={
                  resolvedLegalTransaction &&
                  !legalTransactionPreparedArray.some(
                    lt =>
                      lt.idLegalTransaction ===
                      resolvedLegalTransaction.idLegalTransaction
                  )
                    ? [
                        resolvedLegalTransaction,
                        ...legalTransactionPreparedArray,
                      ]
                    : legalTransactionPreparedArray
                }
                isOptionEqualToValue={(o, v) =>
                  o.idLegalTransaction === v.idLegalTransaction
                }
                getOptionLabel={option => String(option.Title)}
                value={resolvedLegalTransaction ?? null}
                onChange={(_, newValue) => {
                  setEditTask({
                    ...editTask,
                    idLegalTransaction: newValue
                      ? newValue.idLegalTransaction
                      : null,
                  });
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    label='Vorgang'
                    placeholder='Suche nach Vorgängen...'
                    size='small'
                    variant='outlined'
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {isLoadingLegalTransactions ? (
                            <CircularProgress color='inherit' size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                    onChange={debouncedSearchLegalTransaction}
                  />
                )}
                disabled={disableEdit}
              />
            )}
          </Grid>
          <Grid item sm={6}>
            {legalClient ? (
              <>{`${legalClient.FirstName} ${legalClient.LastName}`}</>
            ) : (
              <Autocomplete
                loading={isLoadingLegalClients}
                noOptionsText={
                  searchUriLegalClients === undefined
                    ? 'Tippen, um zu suchen…'
                    : isLoadingLegalClients
                      ? 'Suche…'
                      : 'Keine Treffer'
                }
                options={clientOptions}
                getOptionLabel={opt => formatClientName(opt)}
                isOptionEqualToValue={(o, v) =>
                  o.idLegalClient === v.idLegalClient
                }
                value={resolvedLegalClient ?? null}
                onChange={(_, newValue) =>
                  setEditTask({
                    ...editTask,
                    idLegalClient: newValue?.idLegalClient ?? null,
                  })
                }
                disabled={disableEdit}
                renderInput={params => (
                  <TextField
                    {...params}
                    label='Mandant'
                    placeholder='Suche nach Mandanten...'
                    size='small'
                    variant='outlined'
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {isLoadingLegalClients ? (
                            <CircularProgress color='inherit' size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                    onChange={debouncedSearchLegaClient}
                  />
                )}
                renderOption={(props, opt) => {
                  const isHighlighted = highlightedClientIds.includes(
                    opt.idLegalClient
                  );

                  return (
                    <ListItem
                      {...props}
                      key={opt.idLegalClient}
                      sx={{
                        gap: 1.2,
                        borderLeft: isHighlighted
                          ? '4px solid #1976d2'
                          : '4px solid transparent',
                        backgroundColor: isHighlighted
                          ? 'rgba(25, 118, 210, 0.08)'
                          : 'transparent',
                        borderRadius: 1,
                        mb: 0.3,
                      }}
                    >
                      {opt.idBusinessForm === 1 ? (
                        <Person
                          fontSize='small'
                          color={isHighlighted ? 'primary' : 'inherit'}
                        />
                      ) : (
                        <Business
                          fontSize='small'
                          color={isHighlighted ? 'primary' : 'inherit'}
                        />
                      )}

                      <ListItemText
                        primary={formatClientName(opt)}
                        secondary={
                          isHighlighted
                            ? '✔ Zugehörig zum ausgewählten Vorgang'
                            : opt.idBusinessForm === 1
                              ? 'Private Person'
                              : 'Firma / Organisation'
                        }
                        primaryTypographyProps={{
                          fontWeight: isHighlighted ? 700 : 500,
                        }}
                        secondaryTypographyProps={{
                          fontSize: 11,
                          color: isHighlighted
                            ? 'primary.main'
                            : 'text.secondary',
                        }}
                      />
                    </ListItem>
                  );
                }}
              />
            )}
          </Grid>
        </Grid>

        {/* DATUMSBEREICH */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <CustomeTextField
              attr='Deadline_at'
              label='Fälligkeitsdatum'
              required
              object={editTask}
              setObject={setEditTask}
              type='date'
              disabled={disableEdit}
            />
          </Grid>

          {reminderMode !== 'custom' && (
            <Grid item xs={12}>
              <TextField
                size='small'
                select
                fullWidth
                label='Erinnerung'
                value={reminderMode}
                onChange={e => {
                  const mode = e.target.value;
                  setReminderMode(mode);
                  const date = getReminderDate(mode);
                  setEditTask(prev => ({
                    ...prev,
                    Reminder_at: date ? date.toISOString().split('T')[0] : null,
                  }));
                }}
                disabled={disableEdit}
              >
                {getReminderOptionsWithDisabled().map(opt => (
                  <MenuItem
                    key={opt.value}
                    value={opt.value}
                    disabled={opt.disabled}
                  >
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}

          {/* Custom-DatePicker */}
          {reminderMode === 'custom' && (
            <Grid item xs={12}>
              <TextField
                size='small'
                fullWidth
                type='date'
                disabled={disableEdit}
                label='Eigenes Erinnerungsdatum'
                inputProps={{
                  max: editTask.Deadline_at
                    ? new Date(editTask.Deadline_at)
                        .toISOString()
                        .substring(0, 10)
                    : undefined,
                }}
                value={
                  customReminder
                    ? customReminder.toISOString().substring(0, 10)
                    : ''
                }
                onChange={e => {
                  const value = e.target.value;
                  if (!value) {
                    setCustomReminder(null);
                    setEditTask(prev => ({ ...prev, Reminder_at: null }));
                    setReminderMode('null');
                  } else {
                    const d = new Date(value);
                    if (isNaN(d.getTime())) {
                      setReminderMode('null');
                    } else {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      setCustomReminder(d);
                      setEditTask(prev => ({
                        ...prev,
                        Reminder_at: d.toISOString().split('T')[0],
                      }));
                    }
                  }
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          )}
        </Grid>
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <CustomeTextField
              attr='Body'
              label='Beschreibung'
              object={editTask}
              setObject={setEditTask}
              type='string'
              rows={5}
              disabled={disableEdit}
            />
          </Grid>
          <Grid item xs={12}>
            <TaskDocuments
              documentArray={editTask.DocumentArray ?? []}
              setDocumentArray={documents =>
                setEditTask(prev => ({ ...prev, DocumentArray: documents }))
              }
              disableEdit={disableEdit}
              isLoading={documentsLoading}
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ display: activeTab === 1 ? 'block' : 'none' }}>
        <TaskComments
          commentArray={editTask.CommentArray ?? []}
          setCommentArray={comments =>
            setEditTask(prev => ({ ...prev, CommentArray: comments }))
          }
          disableEdit={disableEdit}
          isLoading={documentsLoading}
        />
      </Box>

      {editTask.idTask > 0 && (
        <Box sx={{ display: activeTab === 2 ? 'block' : 'none' }}>
          <TaskLogList isLoading={documentsLoading} idTask={editTask.idTask} />
        </Box>
      )}
    </>
  );
};
